import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Users, Trophy, Search, ChevronUp, ChevronDown, Filter } from 'lucide-react';
import { calcolaStatisticheSquadra, calcolaPuntiRanking, trovaSquadraGiocatore } from './util';
import { LinkGiocatore, LinkSquadra } from '../../../components/statistiche/Link';
import { urlEdizione } from '../links';
import '../Statistiche.css';

/**
 * L'elenco delle squadre di una edizione, rotta figlia di `EdizioneLayout`.
 *
 * Le statistiche arrivano dall'Outlet: caricarle qui significherebbe riscaricare l'edizione
 * intera ogni volta che si entra e si esce da questa vista.
 */
const SquadreView = () => {
  const { statistiche, idEdizione } = useOutletContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('puntiTotali');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expanded, setExpanded] = useState(null);

  // Calcola statistiche per ogni squadra
  const getSquadreStats = () => {
    return statistiche.squadre.map(squadra => {
      return calcolaStatisticheSquadra(squadra, statistiche.giocatori, statistiche.partite);
    });
  };

  // Giocatori di una squadra (dal roster del DTO o, in fallback, per associazione)
  const getGiocatoriSquadra = (squadra) => {
    if (squadra.giocatori && squadra.giocatori.length) return squadra.giocatori;
    return statistiche.giocatori.filter(
      (g) => g.squadra === squadra.nome
        || trovaSquadraGiocatore(g, statistiche.squadre)?.nome === squadra.nome
    );
  };

  const toggleExpanded = (nome) => setExpanded((cur) => (cur === nome ? null : nome));

  // Il fair play medio di una squadra di cui nessuno ha raccolto il voto non e' zero: uno zero
  // in tabella si leggerebbe come il giudizio peggiore possibile, invece che come un referto
  // che non e' stato compilato.
  const FairPlay = ({ squadra }) => (
    squadra.fairPlayMedia === null
      ? <>—</>
      : <>{squadra.fairPlayMedia} <em className="stat-cell-totale">({squadra.fairPlayTotale})</em></>
  );

  // Il nome della squadra vive dentro una riga che apre e chiude la rosa: senza fermare l'evento
  // un click sul link lascerebbe aperta la rosa della squadra che si sta abbandonando.
  const SoloLink = ({ className = '', children }) => (
    <span className={className} onClick={(e) => e.stopPropagation()}>{children}</span>
  );

  // Sotto-tabella roster riutilizzata da desktop e mobile
  const RosterGiocatori = ({ squadra }) => {
    const giocatori = [...getGiocatoriSquadra(squadra)].sort(
      (a, b) => calcolaPuntiRanking(b) - calcolaPuntiRanking(a)
    );
    if (!giocatori.length) {
      return <div className="squadra-roster-empty">Nessun giocatore disponibile</div>;
    }
    return (
      <div className="squadra-roster">
        <div className="squadra-roster-row squadra-roster-head">
          <span className="squadra-roster-name">Giocatore</span>
          <span>M</span><span>P</span><span>V</span><span>A</span><span>Pt</span>
        </div>
        {giocatori.map((g, i) => (
          <div key={`${g.nome}-${g.numero}-${i}`} className="squadra-roster-row">
            <span className="squadra-roster-name">
              <LinkGiocatore id={g.idGiocatore} nome={g.nome} cognome={g.cognome} />
              {g.numero != null && <em className="squadra-roster-num">#{g.numero}</em>}
            </span>
            <span>{g.mete || 0}</span>
            <span>{g.pieni || 0}</span>
            <span>{g.vuoti || 0}</span>
            <span>{g.assist || 0}</span>
            <span className="squadra-roster-pts">{calcolaPuntiRanking(g)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Filtra e ordina le squadre
  const squadreStats = getSquadreStats()
    .filter(squadra => 
      squadra.nome.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortField] || 0;
      const bValue = b[sortField] || 0;
      
      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

  // Gestisce l'ordinamento delle colonne
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Componente header tabella con ordinamento
  const SortableHeader = ({ field, children }) => (
    <th 
      className="squadre-table-header sortable"
      onClick={() => handleSort(field)}
    >
      <div className="header-content">
        <span>{children}</span>
        <div className="sort-icons">
          <ChevronUp 
            className={`sort-icon ${sortField === field && sortDirection === 'asc' ? 'active' : ''}`}
          />
          <ChevronDown 
            className={`sort-icon ${sortField === field && sortDirection === 'desc' ? 'active' : ''}`}
          />
        </div>
      </div>
    </th>
  );

  // Componente tabella per desktop
  const SquadreTable = () => (
    <div className="squadre-table-container">
      <table className="squadre-table">
        <thead>
          <tr>
            <th className="squadre-table-header rank-header">#</th>
            <SortableHeader field="nome">Squadra</SortableHeader>
            <th className="squadre-table-header">Gruppo</th>
            <SortableHeader field="numeroGiocatori">Giocatori</SortableHeader>
            <SortableHeader field="partiteTotali">Partite</SortableHeader>
            <SortableHeader field="meteTotali">Mete</SortableHeader>
            <SortableHeader field="pieniTotali">Pieni</SortableHeader>
            <SortableHeader field="vuotiTotali">Vuoti</SortableHeader>
            <SortableHeader field="assistTotali">Assist</SortableHeader>
            <SortableHeader field="falliTotali">Falli</SortableHeader>
            <SortableHeader field="fairPlayMedia">Fair play</SortableHeader>
            <SortableHeader field="puntiTotali">Punti</SortableHeader>
          </tr>
        </thead>
        <tbody>
          {squadreStats.map((squadra, index) => {
            const isOpen = expanded === squadra.nome;
            return (
              <React.Fragment key={squadra.nome}>
                <tr
                  className={`squadre-table-row squadre-row-clickable ${isOpen ? 'is-open' : ''}`}
                  onClick={() => toggleExpanded(squadra.nome)}
                  aria-expanded={isOpen}
                >
                  <td className="rank-cell">
                    <div className={`rank-badge-small ${
                      index + 1 === 1 ? 'first' : index + 1 === 2 ? 'second' : index + 1 === 3 ? 'third' : 'other'
                    }`}>
                      {index + 1}
                    </div>
                  </td>
                  <td className="squadra-name-cell">
                    <div className="squadra-name-content">
                      <ChevronDown className={`squadra-expand-caret ${isOpen ? 'is-open' : ''}`} size={16} />
                      <SoloLink className="squadra-name"><LinkSquadra slug={squadra.slugStorica} nome={squadra.nome} /></SoloLink>
                      {index + 1 <= 3 && (
                        <Trophy className={`table-trophy ${
                          index + 1 === 1 ? 'first' : index + 1 === 2 ? 'second' : 'third'
                        }`} />
                      )}
                    </div>
                  </td>
                  <td className="squadra-name-cell">{squadra.gruppo || ''}</td>
                  <td className="stat-cell">{squadra.numeroGiocatori}</td>
                  <td className="stat-cell">{squadra.partiteTotali}</td>
                  <td className="stat-cell">{squadra.meteTotali}</td>
                  <td className="stat-cell">{squadra.pieniTotali}</td>
                  <td className="stat-cell">{squadra.vuotiTotali}</td>
                  <td className="stat-cell">{squadra.assistTotali}</td>
                  <td className="stat-cell">{squadra.falliTotali}</td>
                  <td className="stat-cell"><FairPlay squadra={squadra} /></td>
                  <td className="stat-cell points-cell">
                    <span className="points-value">{squadra.puntiTotali}</span>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="squadra-expanded-row">
                    <td colSpan={12}>
                      <RosterGiocatori squadra={squadra} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // Componente card compatta per mobile
  const StatSquadraCardCompact = ({ squadra, posizione }) => {
    const isOpen = expanded === squadra.nome;
    return (
    <div className={`squadre-card-compact ${isOpen ? 'is-open' : ''}`}>
      <div
        className="squadre-card-compact-header squadra-row-clickable"
        onClick={() => toggleExpanded(squadra.nome)}
        aria-expanded={isOpen}
      >
        <div className={`squadre-rank-badge-compact ${
          posizione === 1 ? 'first' : posizione === 2 ? 'second' : posizione === 3 ? 'third' : 'other'
        }`}>
          {posizione}
        </div>
        <div className="squadre-card-compact-title">
          <h3><SoloLink><LinkSquadra slug={squadra.slugStorica} nome={squadra.nome} /></SoloLink></h3>
          {squadra.gruppo && <p className="squadra-gruppo-compact">{squadra.gruppo}</p>}
          <p>{squadra.numeroGiocatori} giocatori</p>
        </div>
        <ChevronDown className={`squadra-expand-caret ${isOpen ? 'is-open' : ''}`} size={18} />
      </div>

      <div className="squadre-stats-compact">
        <div className="stat-compact">
          <span className="stat-compact-value">{squadra.partiteTotali}</span>
          <span className="stat-compact-label">Partite</span>
        </div>
        <div className="stat-compact">
          <span className="stat-compact-value">{squadra.meteTotali}</span>
          <span className="stat-compact-label">Mete</span>
        </div>
        <div className="stat-compact">
          <span className="stat-compact-value">{squadra.pieniTotali}</span>
          <span className="stat-compact-label">Pieni</span>
        </div>
        <div className="stat-compact">
          <span className="stat-compact-value">{squadra.vuotiTotali}</span>
          <span className="stat-compact-label">Vuoti</span>
        </div>
        <div className="stat-compact">
          <span className="stat-compact-value">{squadra.assistTotali}</span>
          <span className="stat-compact-label">Assist</span>
        </div>
        <div className="stat-compact">
          <span className="stat-compact-value">{squadra.falliTotali}</span>
          <span className="stat-compact-label">Falli</span>
        </div>
        <div className="stat-compact">
          <span className="stat-compact-value"><FairPlay squadra={squadra} /></span>
          <span className="stat-compact-label">Fair play</span>
        </div>
        <div className="stat-compact points">
          <span className="stat-compact-value">{squadra.puntiTotali}</span>
          <span className="stat-compact-label">Punti</span>
        </div>
      </div>

      {isOpen && (
        <div className="squadra-expanded-mobile">
          <RosterGiocatori squadra={squadra} />
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="detail-view">
      <div className="detail-header">
        <Link to={urlEdizione(idEdizione)} className="back-button">
          <ArrowLeft size={20} />
          Torna all'edizione
        </Link>
        <div className="detail-title">
          <Users className="detail-icon" />
          <h2>Statistiche Squadre ({squadreStats.length})</h2>
        </div>
      </div>

      {/* Barra di ricerca */}
      <div className="squadre-search-container">
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Cerca squadra..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Verifica se ci sono risultati */}
      {squadreStats.length === 0 ? (
        <div className="no-results">
          <Filter className="no-results-icon" />
          <h3>Nessuna squadra trovata</h3>
          <p>Prova a modificare il termine di ricerca</p>
        </div>
      ) : (
        <>
          {/* Tabella per desktop */}
          <div className="squadre-desktop-view">
            <SquadreTable />
          </div>

          {/* Card per mobile */}
          <div className="squadre-mobile-view">
            <div className="squadre-grid-compact">
              {squadreStats.map((squadra, index) => (
                <StatSquadraCardCompact 
                  key={squadra.nome} 
                  squadra={squadra} 
                  posizione={index + 1}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SquadreView;